import React from "react";
import {
  Badge,
  BlockStack,
  Button,
  InlineStack,
  Text,
  Tooltip,
} from "@medentic/polaris-react";
import { Progress, Tone } from "@medentic/polaris-react/build/ts/src/components/Badge";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { RefreshIcon } from "@medentic/polaris-icons";
import useApiRequest from "@shared/hooks/common/useApiRequest";
export interface PatientJsonBadge {
  type: string;
  label: string;
  tooltip?: string;
  ctaLabel?: string;
  ctaUrl?: string;
  progress?: Progress;
  tone?: Tone;
}

interface JSONBadgeRendererProps {
  label?: string;
  badge: PatientJsonBadge;
  allowRefresh?: boolean;
  patientId: string;
}

const JSONBadgeRenderer: React.FC<JSONBadgeRendererProps> = ({
  label,
  badge,
  allowRefresh = false,
  patientId,
}) => {
  const queryClient = useQueryClient();
  const { GET } = useApiRequest();
  const { data, isFetching, refetch } = useQuery({
    queryKey: ["badgeStatus", badge.ctaUrl],
    queryFn: () => GET<PatientJsonBadge>(badge.ctaUrl ?? "", false, true),
    enabled: false,
  });

  const handleRefresh = () => {
    refetch().then(() => {
      queryClient.invalidateQueries({
        queryKey: ["patientDetails", patientId],
      });
      queryClient.invalidateQueries({ queryKey: ["patients"] });
    });
  };

  const currentBadge = data || badge;

  return (
    <BlockStack gap="200">
      {label && (
        <Text variant="bodyMd" as="p">
          {label}
        </Text>
      )}
      <InlineStack gap="200" align="start" blockAlign="center">
        <Tooltip content={currentBadge.tooltip}>
          <span className="tooltip_container">
            <Badge
              progress={currentBadge.progress as Progress}
              tone={currentBadge.tone as Tone}
            >
              {currentBadge.label}
            </Badge>
          </span>
        </Tooltip>
        {allowRefresh && badge.ctaUrl && (
          <Tooltip content={badge.ctaLabel ?? ""}>
            <Button
              size="slim"
              onClick={handleRefresh}
              loading={isFetching}
              disabled={isFetching}
              icon={RefreshIcon}
              variant="tertiary"
            ></Button>
          </Tooltip>
        )}
      </InlineStack>
    </BlockStack>
  );
};

export default JSONBadgeRenderer;
